/**
 * Dropdown list options for various form fields
 * Constants are centralized in @tutor/types (shared monorepo package).
 */

// Import centralized education level definitions
import {
  EDUCATION_LEVELS as EDUCATION_LEVELS_OPTIONS,
  LEVEL_SUBJECTS as LEVEL_SUBJECTS_ENUM,
  EDUCATION_LEVEL_LABELS,
  EDUCATION_LEVEL_DEFINITIONS,
  formatEducationLevel,
  TUITION_EXPERIENCE_YEARS,
  STATE_OPTIONS,
  STATE_DISTRICTS,
} from '@tutor/types';

export { TUITION_EXPERIENCE_YEARS };

// Re-export shared dropdown options from @tutor/types
export {
  NATIONALITIES,
  UNIVERSITIES,
  MAJOR_OPTIONS,
  CURRENT_STATUS_OPTIONS,
  EDUCATION_LEVELS_HIGHEST,
  TEACHING_LANGUAGES,
  TUTOR_EXPERIENCE_TYPES,
  TUITION_TYPE_OPTIONS,
} from '@tutor/types';

// Public search uses the full system-aware label so duplicate levels remain distinguishable.
export const EDUCATION_LEVELS = EDUCATION_LEVELS_OPTIONS.map((option) => ({
  ...option,
  label: formatEducationLevel(option.value, {
    includeSystem: true,
    useShortSystemLabel: true,
  }),
}));
export const LEVEL_SUBJECTS = LEVEL_SUBJECTS_ENUM;

/**
 * Lessons per week options
 */
export const LESSONS_PER_WEEK = [
  { value: 1, label: '1 lesson/week' },
  { value: 2, label: '2 lessons/week' },
  { value: 3, label: '3 lessons/week' },
  { value: 4, label: '4 lessons/week' },
  { value: 5, label: '5 lessons/week' },
];

/**
 * Lesson duration options (in hours)
 */
export const LESSON_DURATION = [
  { value: 1, label: '1 hour' },
  { value: 1.5, label: '1.5 hours' },
  { value: 2, label: '2 hours' },
  { value: 2.5, label: '2.5 hours' },
  { value: 3, label: '3 hours' },
];

/**
 * Teaching languages for parent registration
 */
export const PARENT_REGISTRATION_LANGUAGES = ['English', 'Malay', 'Chinese', 'Tamil'];

/**
 * Malaysian states for location selection
 */
export const MALAYSIAN_STATES = [
  'Johor',
  'Kedah',
  'Kelantan',
  'Kuala Lumpur',
  'Labuan',
  'Malacca',
  'Negeri Sembilan',
  'Pahang',
  'Penang',
  'Perak',
  'Perlis',
  'Putrajaya',
  'Sabah',
  'Sarawak',
  'Selangor',
  'Terengganu',
];

/**
 * Malaysian states — re-exported from @tutor/types (canonical source)
 */
export { STATE_OPTIONS as PARENT_REGISTRATION_STATES, STATE_DISTRICTS as MALAYSIAN_DISTRICTS };

/**
 * Areas in selected states (sample - can be expanded)
 */
export const AREAS_BY_STATE: Record<string, string[]> = {
  Kuala_Lumpur: [
    'Bukit Bintang',
    'Cheras',
    'Sentosa',
    'Petaling Jaya',
    'Ampang',
  ],
  Selangor: [
    'Petaling Jaya',
    'Subang Jaya',
    'Shah Alam',
    'Klang',
    'Kajang',
  ],
  Penang: [
    'George Town',
    'Butterworth',
    'Bayan Lepas',
    'Bukit Mertajam',
  ],
  // Add more states and areas as needed
};

/**
 * Gender options
 */
export const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

/**
 * Qualification/experience levels for tutors
 */
export const QUALIFICATION_LEVELS = [
  { value: 'high-school', label: 'High School Graduate' },
  { value: 'diploma', label: 'Diploma' },
  { value: 'bachelor', label: 'Bachelor\'s Degree' },
  { value: 'master', label: 'Master\'s Degree' },
  { value: 'phd', label: 'PhD' },
];

/**
 * Degree types for qualifications
 */
export const DEGREE_TYPES = [
  'SPM',
  'STPM',
  'A-Level',
  'Foundation',
  'Diploma',
  'Associate Degree',
  'Bachelor\'s Degree',
  'Master\'s Degree',
  'PhD',
  'Professional Certificate',
  'Other',
];

/**
 * Year of experience options
 */
export const EXPERIENCE_YEARS = [
  { value: '0-1', label: '0-1 years' },
  { value: '1-3', label: '1-3 years' },
  { value: '3-5', label: '3-5 years' },
  { value: '5-10', label: '5-10 years' },
  { value: '10+', label: '10+ years' },
];

/**
 * Mapping between education level values and their display labels
 * Imported from centralized EDUCATION_LEVEL_LABELS in @tutor/types
 */
export const LEVEL_TO_SUBJECT_LABEL: Record<string, string> = EDUCATION_LEVEL_LABELS;

/**
 * Subjects organized by education level
 */
/**
 * Subjects organized by education level (keyed by display label)
 * Generated from centralized EDUCATION_LEVEL_DEFINITIONS in @tutor/types
 */
export const SUBJECTS_BY_LEVEL: Record<string, string[]> = (() => {
  const result: Record<string, string[]> = {};
  EDUCATION_LEVEL_DEFINITIONS.forEach((def) => {
    result[def.label] = def.subjects;
  });
  return result;
})();

/**
 * All unique subjects (flat list for search)
 */
export const ALL_SUBJECTS = Array.from(
  new Set(Object.values(SUBJECTS_BY_LEVEL).flat())
).sort();

// TEACHING_LANGUAGES, CURRENT_STATUS_OPTIONS, EDUCATION_LEVELS_HIGHEST,
// TUTOR_EXPERIENCE_TYPES are re-exported from @tutor/types above.

export const TEACHER_RELATED_EXPERIENCE_TYPES = [
  'Current School Teacher',
  'Retired School Teacher',
  'Kindergarten Teacher',
];

export const hasTeacherRelatedExperience = (types: string[] = []): boolean =>
  types.some((type) => TEACHER_RELATED_EXPERIENCE_TYPES.includes(type));

// TUITION_EXPERIENCE_YEARS is re-exported from @tutor/types above

/**
 * Public exam types for results
 */
export const PUBLIC_EXAM_TYPES = [
  { value: 'spm', label: 'SPM' },
  { value: 'stpm', label: 'STPM' },
  { value: 'igcse', label: 'IGCSE' },
  { value: 'a-level', label: 'A-Level' },
  { value: 'ib', label: 'IB' },
  { value: 'foundation', label: 'Foundation' },
];

/**
 * Exam results subjects mapped by exam type (compulsory and elective)
 */
export const EXAM_SUBJECTS_BY_TYPE: Record<string, { compulsory: string[]; elective: string[] }> = {
  spm: {
    compulsory: ['Bahasa Malaysia', 'English', 'Mathematics'],
    elective: [
      'Bahasa Malaysia',
      'English',
      'Mathematics',
      'Additional Mathematics',
      'Physics',
      'Chemistry',
      'Biology',
      'Science',
      'History',
      'Geography',
      'Accounting',
      'Economics',
      'Business Studies',
      'Islamic Studies',
      'Chinese',
      'Tamil',
      'Computer Science',
      'ICT',
    ],
  },
  stpm: {
    compulsory: [],
    elective: [
      'Mathematics T',
      'Mathematics M',
      'Physics',
      'Chemistry',
      'Biology',
      'Further Mathematics',
      'General Studies',
      'Bahasa Malaysia',
      'English',
      'Chinese',
      'Economics',
      'Accounting',
      'Business Studies',
      'History',
      'Geography',
      'Computer Science',
    ],
  },
  igcse: {
    compulsory: ['English Language', 'Mathematics'],
    elective: [
      'English Language',
      'English Literature',
      'Mathematics',
      'Additional Mathematics',
      'Physics',
      'Chemistry',
      'Biology',
      'Combined Science',
      'Chinese',
      'Economics',
      'Business Studies',
      'Accounting',
      'History',
      'Geography',
      'Computer Science',
    ],
  },
  'a-level': {
    compulsory: [],
    elective: [
      'Mathematics',
      'Further Mathematics',
      'Physics',
      'Chemistry',
      'Biology',
      'Economics',
      'Business Studies',
      'Accounting',
      'English Literature',
      'History',
      'Geography',
      'Computer Science',
      'Psychology',
      'Sociology',
    ],
  },
  ib: {
    compulsory: [],
    elective: [
      'Mathematics',
      'Physics',
      'Chemistry',
      'Biology',
      'Economics',
      'Business Management',
      'English Literature',
      'English Language',
      'History',
      'Geography',
      'Computer Science',
      'Philosophy',
      'Psychology',
    ],
  },
  foundation: {
    compulsory: [],
    elective: [
      'Mathematics',
      'Physics',
      'Chemistry',
      'Biology',
      'English',
      'Computer Science',
      'Economics',
      'Accounting',
      'Business Studies',
    ],
  },
};

/**
 * Grade scales for exam results
 */
export const EXAM_GRADES = [
  'A+',
  'A',
  'A-',
  'B+',
  'B',
  'B-',
  'C+',
  'C',
  'C-',
  'D',
  'E',
  'F',
  'Grade 1',
  'Grade 2',
  'Grade 3',
  'Grade 4',
  'Grade 5',
  'Grade 6',
  'Grade 7',
];

/**
 * Teaching style options
 */
export const TEACHING_STYLE_OPTIONS = {
  'I am': [
    'Patient',
    'Strict',
    'Meticulous',
    'Caring',
  ],
  'I can': [
    'Explain complex concepts clearly',
    'Adapt to different learning styles',
    'Create engaging lessons',
    'Provide personalized attention',
    'Break down difficult topics',
  ],
  'I know': [
    'Latest exam trends',
    'Effective study techniques',
    'Common student difficulties',
    'Multiple teaching approaches',
  ],
};

/**
 * Malaysian public examination types
 */
export const MALAYSIAN_EXAMS = [
  'SPM',
  'STPM',
  'UEC',
  'IGCSE',
  'O-Level',
  'A-Level',
  'Matriculation',
  'Foundation',
];

/**
 * Standardised Malaysian school subjects (core and elective)
 */
export const MALAYSIAN_SUBJECTS = [
  'Bahasa Melayu',
  'English',
  'Mathematics',
  'Science',
  'Sejarah',
  'Pendidikan Moral',
  'Pendidikan Islam',
  'Additional Mathematics',
  'Physics',
  'Chemistry',
  'Biology',
  'Prinsip Perakaunan',
  'Ekonomi',
  'Perniagaan',
  'Geografi',
  'Pendidikan Jasmani dan Kesihatan',
  'Seni Visual',
  'Muzik',
  'Bahasa Cina',
  'Bahasa Tamil',
  'Bahasa Arab',
  'Bahasa Iban',
  'Bahasa Kadazandusun',
];

// MALAYSIAN_STATES_AND_DISTRICTS removed — use STATE_DISTRICTS from @tutor/types

/**
 * Year options for exam results (1990 to current year)
 */
export const EXAM_YEARS = (() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear; year >= 1990; year--) {
    years.push({ value: year, label: year.toString() });
  }
  return years;
})();
